import { Injectable, inject, resource, computed, signal } from '@angular/core';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, getDoc, limit } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import { AccessLogService } from './access-log.service';
import { SiteManagementDepartment } from '../../shared/interfaces';

@Injectable({
  providedIn: 'root'
})
export class SiteManagementDepartmentService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);
  private readonly accessLogService = inject(AccessLogService);

  private readonly manualError = signal<string | null>(null);

  private async logAction(action: string, resourcePath: string, status: 'success' | 'error'): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    await this.accessLogService.addLog({
      userId: user.uid,
      userName: user.email || '',
      action,
      resource: resourcePath,
      status
    });
  }

  private readonly departmentsResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole()
    }),
    loader: async ({ params: { orgId, role } }) => {
      if (!orgId && role !== 'super_admin') return [];
      try {
        if (role === 'super_admin') {
          return await this.fetchAllDepartmentsFromFirestore();
        }
        return await this.fetchDepartmentsFromFirestore(orgId!);
      } catch (err) {
        console.error('管理部署の取得に失敗しました:', err);
        throw new Error('管理部署の取得に失敗しました');
      }
    }
  });

  public readonly departments = computed(() => this.departmentsResource.hasValue() ? this.departmentsResource.value() : []);
  
  public readonly isLoading = computed(() => 
    this.departmentsResource.status() === 'loading' || 
    this.departmentsResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const manual = this.manualError();
    if (manual) return manual;
    const resErr = this.departmentsResource.error();
    if (resErr) return resErr instanceof Error ? resErr.message : '管理部署一覧の取得エラー';
    return null;
  });

  public reload(): void {
    this.manualError.set(null);
    this.departmentsResource.reload();
  }

  private checkPermission(targetOrgId: string): void {
    if (this.authService.userRole() === 'super_admin') return;
    if (this.authService.userRole() === 'org_admin' && this.authService.userOrganizationId() === targetOrgId) return;
    throw new Error('権限がありません');
  }

  public async getDepartmentsByOrganizationId(orgId: string): Promise<SiteManagementDepartment[]> {
    if (!orgId) return [];
    try {
      this.checkPermission(orgId);
      return await this.fetchDepartmentsFromFirestore(orgId);
    } catch (err) {
      if (err instanceof Error && err.message.includes('権限')) {
        this.manualError.set(err.message);
        throw err;
      }
      console.error('指定組織の管理部署取得に失敗しました:', err);
      throw new Error('指定組織の管理部署取得に失敗しました');
    }
  }

  public async createDepartment(orgId: string, name: string): Promise<string> {
    let status: 'success' | 'error' = 'success';
    let docId = '';
    try {
      this.manualError.set(null);
      this.checkPermission(orgId);
      const ref = collection(this.firestore, 'site_management_departments');
      const docRef = await addDoc(ref, {
        organizationId: orgId,
        name,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null
      });
      docId = docRef.id;
      return docId;
    } catch (err) {
      status = 'error';
      let errorMessage = '管理部署の作成に失敗しました';
      if (err instanceof Error && err.message.includes('権限')) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('department.create', docId ? `site_management_departments/${docId}` : 'site_management_departments', status);
    }
  }

  public async updateDepartment(id: string, name: string): Promise<void> {
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      const ref = doc(this.firestore, 'site_management_departments', id);
      const docSnap = await getDoc(ref);
      if (!docSnap.exists()) {
        throw new Error('対象の管理部署が見つかりません');
      }
      this.checkPermission(docSnap.data()['organizationId']);
      await updateDoc(ref, {
        name,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      status = 'error';
      let errorMessage = '管理部署の更新に失敗しました';
      if (err instanceof Error && err.message.includes('権限')) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('department.update', `site_management_departments/${id}`, status);
    }
  }

  public async deleteDepartment(id: string): Promise<void> {
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      
      const ref = doc(this.firestore, 'site_management_departments', id);
      const docSnap = await getDoc(ref);
      if (!docSnap.exists()) {
        throw new Error('対象の管理部署が見つかりません');
      }
      this.checkPermission(docSnap.data()['organizationId']);

      const usersRef = collection(this.firestore, 'users');
      const usersQuery = query(usersRef, where('departmentId', '==', id), where('deletedAt', '==', null), limit(1));
      const usersSnapshot = await getDocs(usersQuery);

      if (!usersSnapshot.empty) {
        throw new Error('この管理部署に紐付くユーザーが存在するため削除できません');
      }

      await updateDoc(ref, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      status = 'error';
      let errorMessage = '管理部署の削除に失敗しました';
      if (err instanceof Error && (err.message.includes('削除できません') || err.message.includes('権限'))) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('department.delete', `site_management_departments/${id}`, status);
    }
  }

  private async fetchDepartmentsFromFirestore(orgId: string): Promise<SiteManagementDepartment[]> {
    const ref = collection(this.firestore, 'site_management_departments');
    const q = query(
      ref,
      where('organizationId', '==', orgId),
      where('deletedAt', '==', null)
    );
    const snapshot = await getDocs(q);
    const deps = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SiteManagementDepartment));
    return deps.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }

  private async fetchAllDepartmentsFromFirestore(): Promise<SiteManagementDepartment[]> {
    const ref = collection(this.firestore, 'site_management_departments');
    const q = query(ref, where('deletedAt', '==', null));
    const snapshot = await getDocs(q);
    const deps = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      } as SiteManagementDepartment));
    return deps.sort((a, b) => a.name.localeCompare(b.name, 'ja'));
  }
}
